import { Component, computed, EventEmitter, inject, input, Input, Output } from '@angular/core';
import { ChatGPTService } from '@dash/randy/shared/submodules/chat-gpt/service/chat-gpt.service';
import { select, Store } from '@ngrx/store';
import { CollectionItemModel } from '../../../modules/crud/collections/collection-item/classes/collection-item.model';
import * as CollectionItemReducer from '../../../modules/crud/collections/collection-item/store/reducers/collection-item.reducer';
import { BaseComponent } from '@dash/randy/shared/classes/baseComponent';
import { firstValueFrom, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CollectionItemEditSignalStore } from '@dash/randy/modules/crud/collections/collection-item/store/reducers/collection-item-edit-signal.store';

@Component({
  selector: 'app-quill',
  templateUrl: './quill.component.html',
  styleUrls: ['./quill.component.scss'],
  standalone: false,
})
export class QuillComponent extends BaseComponent {
  private readonly collectionItemEditStore = inject(CollectionItemEditSignalStore, { optional: true });
  readonly forceReadOnly = input(false);
  readonly readOnly = computed(() => this.forceReadOnly() || (this.collectionItemEditStore?.effectiveReadOnly() ?? false));

  @Input() set collectionItemId(value: string) {
    this._collectionItemId = value;
    this.collectionItem$ = this.collectionItemStore.pipe(
      select(CollectionItemReducer.getItemById(this._collectionItemId)),
      takeUntil(this.destroyed$),
    );
  }

  private _collectionItemId: string;

  collectionItem$: Observable<CollectionItemModel>;

  @Output() editorTextChanged = new EventEmitter<string>();
  @Input() inputValue: string;

  @Input() additionalPrompt: string;

  constructor(private collectionItemStore: Store<CollectionItemModel>, private chatGPTService: ChatGPTService) {
    super();
  }

  changedText(text: string): void {
    if (this.readOnly()) {
      return;
    }

    // trackChanges="user" and the directive's ngModelChange stream exclude initialization.
    // Do not skip the first event here: it is now the user's first edit.
    this.editorTextChanged.emit(text);
  }

  handlePaste(event: ClipboardEvent): void {
    if (this.readOnly()) {
      return;
    }

    event.preventDefault();
    const text = event.clipboardData.getData('text/plain');

    const selection = window.getSelection();
    if (!selection.rangeCount) {
      return;
    }
    selection.deleteFromDocument();
    selection.getRangeAt(0).insertNode(document.createTextNode(text));
  }

  async handleChatgpt(): Promise<void> {
    if (this.readOnly()) {
      return;
    }
    const item = await firstValueFrom(this.collectionItem$);

    // get the current text selection
    const text = window.getSelection()?.toString() ?? '';
    let prompt = this.additionalPrompt;
    if (text.length > 0) {
      prompt = `Geneer een ${this.additionalPrompt} voor object.\n${text}`;
    }
    const response = await this.chatGPTService.sendPrompt(item, `Geneer een ${prompt} voor dit object`);
    if (this.readOnly()) {
      return;
    }
    this.inputValue = response;
    // This intentional edit is programmatic, so Quill will not emit ngModelChange for it.
    this.editorTextChanged.emit(response);
  }
}
